import { useCallback, useEffect, useMemo, useState } from "react";
import { usePage } from "@inertiajs/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  RocketLaunchIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { Deploy } from "@shared";
import { Popover, PopoverTrigger, PopoverContent } from "@components/ui/popover";
import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { Copyable } from "@components/ui/copyable";
import { cn } from "@lib/utils";
import DeployTaskList from "@components/deploy/DeployTaskList";
import DeployHistory from "@components/deploy/DeployHistory";
import { NeedsDomainModal } from "@components/deploy/NeedsDomainModal";
import {
  useDeploys,
  useDeployTasks,
  useDeployService,
  useCancelDeploy,
  deploysKeys,
} from "@api/deploys.hooks";
import { useDeployHeartbeat } from "@hooks/useDeployHeartbeat";

const TERMINAL = Deploy.TERMINAL_DEPLOY_STATUSES as readonly string[];

interface DeployPillPageProps {
  project?: { id?: number; uuid?: string };
  [key: string]: unknown;
}

export interface DeployPillProps {
  /** Whether a domain is assigned. Deploying requires one. */
  hasDomain: boolean;
  /** Called when the user tries to deploy with no domain — guide them to set one. */
  onNeedsDomain?: () => void;
}

/**
 * Inline deploy control — a pill in the build toolbar that expands to a popover.
 * Replaces the standalone website deploy page: it manages WEBSITE deploys only
 * (campaign deploys keep their own page). States: idle/live (live URL + "Deploy
 * latest changes" + recent deploys) and in-progress (task checklist + progress +
 * cancel). Deploying needs a site name first; without one, Deploy explains the
 * missing step (NeedsDomainModal) and hands the user to the picker.
 */
export function DeployPill({ hasDomain, onNeedsDomain }: DeployPillProps) {
  const { project } = usePage<DeployPillPageProps>().props;
  const projectId = project?.id;
  const queryClient = useQueryClient();
  const deployService = useDeployService();
  const cancelDeploy = useCancelDeploy();

  const [open, setOpen] = useState(false);
  const [needsDomainOpen, setNeedsDomainOpen] = useState(false);
  const [startedDeployId, setStartedDeployId] = useState<number | undefined>();
  const [isStarting, setIsStarting] = useState(false);

  const { data: deploysData } = useDeploys(projectId ?? 0, 1);
  const deploys = deploysData?.deploys ?? [];
  const liveDeploy = deploys.find((d) => d.is_live);
  const activeWebsiteDeploy = deploys.find(
    (d) => d.deploy_type === "website" && !TERMINAL.includes(d.status)
  );

  const trackedDeployId = startedDeployId ?? activeWebsiteDeploy?.id;
  const { data: tasksData } = useDeployTasks(trackedDeployId);
  const tasks = tasksData?.tasks;
  const deployStatus = tasksData?.deploy_status;
  const isDeploying = !!trackedDeployId && !TERMINAL.includes(deployStatus ?? "pending");

  useDeployHeartbeat(trackedDeployId, deployStatus);

  // When the tracked deploy finishes, refresh the history/live state and stop tracking.
  useEffect(() => {
    if (deployStatus && TERMINAL.includes(deployStatus)) {
      queryClient.invalidateQueries({ queryKey: deploysKeys.lists() });
      setStartedDeployId(undefined);
    }
  }, [deployStatus, queryClient]);

  const phases = useMemo(
    () => (tasks ? Deploy.computePhases(tasks as unknown as Deploy.Task[]) : []),
    [tasks]
  );
  const completedPhases = phases.filter((p) => p.status === "completed").length;
  const totalPhases = phases.length;
  const percent = totalPhases > 0 ? Math.round((completedPhases / totalPhases) * 100) : 0;

  const handleDeploy = useCallback(async () => {
    if (!hasDomain) {
      // Explain the missing step in a modal instead of silently refusing or
      // flickering the picker open mid-click.
      setOpen(false);
      setNeedsDomainOpen(true);
      return;
    }
    if (!projectId) return;
    setIsStarting(true);
    try {
      const deploy = await deployService.create({ projectId, deployType: "website" });
      setStartedDeployId(deploy.id);
      queryClient.invalidateQueries({ queryKey: deploysKeys.lists() });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to start deploy");
    } finally {
      setIsStarting(false);
    }
  }, [hasDomain, projectId, deployService, queryClient]);

  // From the needs-domain modal: close it, then open the picker on the next
  // tick so the dialog has fully torn down — opening the popover in the same
  // commit lets the closing dialog's outside-click swallow it (the flicker).
  const handleChooseSiteName = useCallback(() => {
    setNeedsDomainOpen(false);
    setTimeout(() => onNeedsDomain?.(), 0);
  }, [onNeedsDomain]);

  const handleCancel = useCallback(() => {
    if (trackedDeployId) cancelDeploy.mutate(trackedDeployId);
  }, [trackedDeployId, cancelDeploy]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="deploy-pill"
          className={cn(
            "flex shrink-0 items-center gap-2 rounded-lg border bg-white px-3 py-2.5",
            "text-sm font-medium text-base-600 transition-colors",
            "hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500",
            isDeploying ? "border-amber-300 bg-amber-50" : "border-neutral-300"
          )}
        >
          {isDeploying ? (
            <>
              <Spinner className="size-4 text-amber-600" />
              <span>Deploying</span>
              <span className="tabular-nums text-base-400">
                {completedPhases}/{totalPhases}
              </span>
            </>
          ) : (
            <>
              <RocketLaunchIcon className="size-4 text-base-500" />
              <span>Deploy</span>
              {liveDeploy && <span className="size-2 rounded-full bg-success-500" />}
            </>
          )}
          <ChevronUpIcon className="size-4 text-base-400" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-[480px] rounded-2xl border-neutral-200 p-0 shadow-xl"
      >
        {isDeploying ? (
          <InProgressContent
            tasks={tasks}
            deployStatus={deployStatus}
            completedPhases={completedPhases}
            totalPhases={totalPhases}
            percent={percent}
            onCancel={handleCancel}
            canceling={cancelDeploy.isPending}
          />
        ) : (
          <LiveContent
            isLive={!!liveDeploy}
            liveUrl={liveDeploy?.url ?? null}
            hasDomain={hasDomain}
            onDeploy={handleDeploy}
            isStarting={isStarting}
          />
        )}
      </PopoverContent>

      <NeedsDomainModal
        isOpen={needsDomainOpen}
        onClose={() => setNeedsDomainOpen(false)}
        onConfirm={handleChooseSiteName}
      />
    </Popover>
  );
}

// ── In-progress ─────────────────────────────────────────────────────────────

function InProgressContent({
  tasks,
  deployStatus,
  completedPhases,
  totalPhases,
  percent,
  onCancel,
  canceling,
}: {
  tasks: Parameters<typeof DeployTaskList>[0]["tasks"] | undefined;
  deployStatus: string | undefined;
  completedPhases: number;
  totalPhases: number;
  percent: number;
  onCancel: () => void;
  canceling: boolean;
}) {
  return (
    <div className="flex flex-col gap-4 p-5" data-testid="deploy-pill-in-progress">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-base-500">Deploying your site</h2>
        <span className="flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-600">
          <Spinner className="size-3" />
          In progress
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-base-500">
            Step {Math.min(completedPhases + 1, totalPhases)} of {totalPhases}
          </span>
          <span className="font-semibold text-primary-600">{percent}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
          <div
            className="h-full rounded-full bg-primary-600 transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {tasks && (
        <DeployTaskList tasks={tasks} deployStatus={deployStatus} headerClassName="p-0" hideTitle />
      )}

      <div className="rounded-lg bg-primary-50 px-4 py-3 text-sm leading-snug text-primary-700">
        Your deploy is in progress. Feel free to keep working — your edits are safe. Any changes you
        make will not affect this deploy.
      </div>

      <div className="flex items-center justify-between border-t border-neutral-100 pt-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={canceling}
          data-testid="deploy-pill-cancel"
          className="text-sm text-base-500 hover:text-destructive disabled:opacity-50"
        >
          {canceling ? "Canceling…" : "Cancel deploy"}
        </button>
      </div>
    </div>
  );
}

// ── Idle / live ───────────────────────────────────────────────────────────────

function LiveContent({
  isLive,
  liveUrl,
  hasDomain,
  onDeploy,
  isStarting,
}: {
  isLive: boolean;
  liveUrl: string | null;
  hasDomain: boolean;
  onDeploy: () => void;
  isStarting: boolean;
}) {
  return (
    <div className="flex flex-col gap-4 p-5" data-testid="deploy-pill-live">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-base-500">Deploy</h2>
        {isLive && (
          <span className="flex items-center gap-1.5 rounded-full bg-success-50 px-3 py-1 text-xs font-medium text-success-700">
            <span className="size-2 rounded-full bg-success-500" />
            Live
          </span>
        )}
      </div>

      {isLive && liveUrl && (
        <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
          <GlobeAltIcon className="size-5 shrink-0 text-base-400" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[10px] font-medium uppercase tracking-wide text-base-400">
              Live site
            </span>
            <Copyable text={liveUrl.startsWith("http") ? liveUrl : `https://${liveUrl}`} className="gap-2">
              <Copyable.Text className="truncate text-sm font-medium text-base-600" />
              <Copyable.Trigger className="text-base-400 hover:text-base-600" />
              <a
                href={liveUrl.startsWith("http") ? liveUrl : `https://${liveUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base-400 transition-colors hover:text-base-600"
              >
                <ArrowTopRightOnSquareIcon className="size-4" />
              </a>
            </Copyable>
          </div>
        </div>
      )}

      <Button
        onClick={onDeploy}
        disabled={isStarting}
        data-testid="deploy-pill-deploy"
        className="w-full justify-center gap-2 bg-base-600 text-white hover:bg-base-700"
      >
        <RocketLaunchIcon className="size-4" />
        {isStarting ? "Starting…" : isLive ? "Deploy latest changes" : "Deploy your site"}
      </Button>

      {!hasDomain && (
        <p className="text-xs text-base-400" data-testid="deploy-pill-needs-domain">
          Choose your site name first to deploy.
        </p>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-base-400">
          Recent deploys
        </span>
        <DeployHistory compact />
      </div>
    </div>
  );
}
